import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege';
import type { DataSourceName } from 'cadenza/repository/model/data-source-name';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id';
import type { EnrichmentDto } from 'cadenza/workbook/enrichment/model/enrichment-dto';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id';
import type { DataAcquisitionActionType } from 'cadenza/repository/model/data-acquisition-action-type';
import type { RepositoryName } from 'cadenza/repository/model/repository-name';
import type { AttributeRelationGroupDto } from 'cadenza/workbook/api/attribute-relation-group-dto';
import type { ProjectId } from 'cadenza/repository/model/project-id';
import type { ObjecttypeBoundState } from 'cadenza/workbook/model/objecttype-bound-state';
import type { ProjectKind } from 'cadenza/repository/model/project-kind';
import type { RepositoryItemDto } from 'cadenza/repository/model/repository-item-dto';

/** @see net/disy/cadenza/workbook/web/dto/ObjecttypeDto.java */
export interface Objecttype extends RepositoryItemDto<'OBJECTTYPE'> {
  printName: string;
  /** Name of data source this object type belongs to. */
  dataSourceName: DataSourceName;
  /** Name of the repository the data source belongs to */
  dataSourceRepositoryName: RepositoryName;
  attributes: AttributeId[];
  /** privileges of this objecttype for current user */
  currentUserPrivileges: ObjectPrivilege[];
  /** Map of objecttype related properties. */
  properties?: { [x: string]: string | number | boolean | null };
  /**
   * If according DataAcquisitionActionType is supported, then the objecttype allows data acquisition
   * including adding, editing and deletion of features.
   */
  possibleDataAcquisitionActionTypes?: DataAcquisitionActionType[];
  /** Bound state of this object type */
  boundState: ObjecttypeBoundState;
  enrichments?: EnrichmentDto[];
  visible: boolean;
  isCombinedObjecttype?: boolean;
  sourceObjecttypeId?: ObjecttypeIdString;
  isMarkedAsTemplate: boolean;
  attributeRelationGroups: AttributeRelationGroupDto[];
  underlyingBasicMainObjecttypeId: ObjecttypeIdString;
  projectId?: ProjectId;
  projectKind?: ProjectKind;
  projectPrintName?: string;
}

/**
 * Do you really mean to use this method? It will only say 'true' if the given objecttype
 * is permanently bound to the workbook. Thus, any new objecttype where the objecttype has just been
 * created and the workbook hasn't been saved will be just transiently bound and not included here.
 * @param objecttype The OT to test
 */
export function isObjecttypePermanentAndBound(objecttype: Objecttype) {
  return objecttype.boundState === 'BOUND_TO_WORKBOOK';
}

/**
 * You will need this version if you care about the OT being in the workbook storage, independent
 * of it being already saved and thus becoming "permanently" bound or still being "transient".
 * @param objecttype The OT to test
 */
export function isObjecttypeBound(objecttype: Pick<Objecttype, 'boundState'>) {
  return objecttype.boundState !== 'UNBOUND';
}

export function isObjecttypeBoundToProject(objecttype: Pick<Objecttype, 'boundState'>) {
  return objecttype.boundState === 'BOUND_TO_PROJECT';
}

/**
 * Do the objecttype bounds indicate that the objecttype has a presence in the management center?
 * Note that permissions etc. could still prevent a user from accessing the objecttype in the MC.
 */
export function isObjecttypeBoundManageable(objecttype: Pick<Objecttype, 'boundState'>) {
  return !isObjecttypeBound(objecttype) || isObjecttypeBoundToProject(objecttype);
}
